/**
 * MIDI data parsing utilities.
 *
 * Transforms raw JSON from the server into GameNote objects that the
 * game engine can consume directly.
 */

import { MidiTrack, MidiNote } from '../types';
import { GameNote } from '../types';
import { v4 as uuidv4 } from 'uuid';

/**
 * Parse a MidiTrack from the server and convert its notes to GameNote
 * objects, sorted by time ascending.
 *
 * @param track - MidiTrack received from the API
 * @returns Sorted array of GameNote objects ready for the game engine
 */
export function parseMidiTrack(track: MidiTrack): GameNote[] {
  if (!track || !Array.isArray(track.notes)) {
    console.warn('[midiParser] Invalid track or empty notes array');
    return [];
  }

  return [...track.notes]
    .sort((a: MidiNote, b: MidiNote) => a.time - b.time)
    .map((note: MidiNote): GameNote => ({
      id: note.id ?? uuidv4(),
      time: note.time,
      lane: Math.max(0, Math.min(5, note.lane)),
      y: 0,          // Will be set by game loop
      judged: false,
    }));
}

/**
 * Filter notes to a specific time range.
 * Used for windowed rendering – only return notes visible on screen.
 *
 * @param notes         - Full sorted note array
 * @param startMs       - Window start in ms
 * @param endMs         - Window end in ms
 */
export function notesInWindow(
  notes: GameNote[],
  startMs: number,
  endMs: number
): GameNote[] {
  return notes.filter((n) => n.time >= startMs && n.time <= endMs);
}

/**
 * Find the earliest note index that falls within the hit window of
 * the current song time on a specific lane.
 *
 * @param notes      - All notes (sorted by time)
 * @param lane       - Lane index to check
 * @param currentMs  - Current song time in ms
 * @param windowMs   - Total hit window in ms (e.g. 200)
 * @returns Index of closest note, or -1 if none found
 */
export function findNearestNoteIndex(
  notes: GameNote[],
  lane: number,
  currentMs: number,
  windowMs: number
): number {
  let bestIdx = -1;
  let bestDiff = Infinity;

  for (let i = 0; i < notes.length; i++) {
    const note = notes[i];
    if (note.judged) continue;
    if (note.lane !== lane) continue;

    const diff = Math.abs(note.time - currentMs);
    if (diff <= windowMs / 2 && diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  }

  return bestIdx;
}

/**
 * Generate a simple demo track for development / offline use.
 */
export function generateDemoTrack(): MidiTrack {
  const bpm = 128;
  const beatMs = 60_000 / bpm;
  const notes: MidiNote[] = [];

  for (let beat = 0; beat < 32; beat++) {
    const lane = beat % 6;
    notes.push({
      id: uuidv4(),
      time: Math.round(beat * beatMs),
      pitch: 60 + lane * 2,
      duration: Math.round(beatMs * 0.4),
      velocity: 80,
      lane,
    });

    // Add syncopated notes every 4 beats
    if (beat % 4 === 0) {
      const offbeatLane = (lane + 3) % 6;
      notes.push({
        id: uuidv4(),
        time: Math.round(beat * beatMs + beatMs * 0.5),
        pitch: 60 + offbeatLane * 2,
        duration: Math.round(beatMs * 0.3),
        velocity: 60,
        lane: offbeatLane,
      });
    }
  }

  return {
    id: 'demo',
    title: 'Demo Track',
    artist: 'RhythmSense',
    bpm,
    duration: 32 * beatMs / 1000,
    notes,
    timeSignatureNumerator: 4,
    timeSignatureDenominator: 4,
  };
}
